import {
  useGetPermanentEnsembles,
  useUpdatePermanentEnsemble,
} from "@/api/permanent-ensemble";
import * as AF from "@/components/form-items/AbstractForm";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { PermanentEnsemble } from "@/lib/types";
import { useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

type EnsembleSettingsProps = {
  form: UseFormReturn<PermanentEnsemble>;
};

const EnsembleSettings = ({ form }: EnsembleSettingsProps) => {
  const { t } = useTranslation();

  return (
    <AF.AbstractForm
      form={form}
      tabs={[
        { value: "generalInformation", label: t("artworksGeneralInfo") },
        { value: "documents", label: t("entryDocuments") },
        { value: "contact", label: t("contactFormTitle") },
      ]}
      classname="p-0 m-0 max-w-[800px]"
    >
      <AF.Tab value="generalInformation" showHeader={false}>
        <AF.Col>
          <AF.Text name="ensemblesDescriptionNomEnsemble" disabled />
          <Separator className="my-2" />
          <AF.Text name="ensemblesDescriptionAuteurEnsemble" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesDescriptionOperateurPrincipalEnsemble" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesDescriptionOperateurAutre" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesDescriptionSignataire" />
        </AF.Col>
      </AF.Tab>
      <AF.Tab value="documents" showHeader={false}>
        <AF.Col>
          <AF.Text name="ensemblesTitreDocument1" />
          <Separator className="my-2" />
          <AF.TextArea className="h-40" name="ensemblesText1Document1" />
          <Separator className="my-2" />
          <AF.TextArea className="h-40" name="ensemblesText2Document1" />
          <Separator className="my-2" />

          <AF.Text name="ensemblesTitreDocument2" />
          <Separator className="my-2" />
          <AF.TextArea className="h-40" name="ensemblesText1Document2" />
          <Separator className="my-2" />
          <AF.TextArea className="h-40" name="ensemblesText2Document2" />
          <Separator className="my-2" />
        </AF.Col>
      </AF.Tab>
      <AF.Tab value="contact" showHeader={false}>
        <AF.Col>
          <AF.Text name="ensemblesCoordonneesAuteurAdresse1" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesCoordonneesAuteurAdresse2" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesCoordonneesAuteurCodePostal" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesCoordonneesAuteurVille" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesCoordonneesAuteurPays" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesCoordonneesAuteurTelephone" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesCoordonneesAuteurMail" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesCoordonneesAuteurSiteWeb" />
          <Separator className="my-2" />
          <AF.Text name="ensemblesLegalsText" />
          <Separator className="my-2" />
          <AF.RichText name="ensemblesDescriptionNotesEnsemble" />
        </AF.Col>
      </AF.Tab>
    </AF.AbstractForm>
  );
};

type EnsembleSettingsFormProps = {
  ensembles: PermanentEnsemble[];
};

const EnsembleSettingsForm = ({ ensembles }: EnsembleSettingsFormProps) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<string>(ensembles?.[0]?.id);
  const { mutateAsync: updatePermanentEnsemble } = useUpdatePermanentEnsemble();

  const form = useForm({
    defaultValues: ensembles?.[0],
  });

  useEffect(() => {
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, form, ensembles]);

  const resetForm = () => {
    const foundEnsemble = ensembles.find(
      (ensemble) => ensemble.id === selectedTab,
    );
    if (foundEnsemble) {
      form.reset(foundEnsemble);
    }
  };

  const clickReset = () => {
    resetForm();
    toast({
      title: t("settingsResetToastTitle"),
      description: t("settingsResetToastDescription"),
      duration: 3000,
      variant: "default",
    });
  };

  const saveForm = async (data: PermanentEnsemble) => {
    updatePermanentEnsemble(data, {
      onSuccess: () => {
        toast({
          title: t("settingsSavedToastTitle"),
          description: t("settingsSavedToastDescription"),
          duration: 3000,
          variant: "success",
        });
        form.reset({}, { keepValues: true });
      },
    });
  };

  return (
    <div>
      <div className="flex flex-row justify-between items-center">
        <h2 className="font-semibold font-[Instrument Sans] text-[32px]">
          {t("mySettings")}
        </h2>

        <div className="flex flex-row items-center gap-4">
          <Button variant="outline" onClick={clickReset}>
            {t("cancel")}
          </Button>
          <Button onClick={form.handleSubmit(saveForm)}>
            {t("saveButton")}
          </Button>
        </div>
      </div>

      <Separator className="mt-5 mb-6" />

      <Tabs
        value={selectedTab}
        onValueChange={setSelectedTab}
        className="flex flex-col gap-5"
      >
        <TabsList>
          {ensembles.map(({ id, ensemblesDescriptionNomEnsemble }) => (
            <TabsTrigger key={`trigger-${id}`} value={id} className="text-xs">
              {ensemblesDescriptionNomEnsemble}
            </TabsTrigger>
          ))}
        </TabsList>
        {ensembles.map((ensemble) => (
          <TabsContent key={`content-${ensemble.id}`} value={ensemble.id}>
            <EnsembleSettings form={form} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export const SettingsPage = () => {
  const { data: ensembles } = useGetPermanentEnsembles();

  if (!ensembles) {
    return null;
  }

  return <EnsembleSettingsForm ensembles={ensembles} />;
};
